import { t } from "@lingui/macro";
import {
  ChevronDown,
  Drama,
  Loader2,
  Sparkles,
  SpellCheck,
  Undo2,
  WandSparkles,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { Editor } from "@tiptap/react";
import { useState } from "react";

import { toast } from "../hooks/use-toast";
import { useTargetJob } from "../hooks/useTargetJob";
import { aiService } from "../services/aiService";

type Action = "improve" | "fix" | "tone";
type Mood = "casual" | "professional" | "confident" | "friendly";

type Props = {
  /** The rich-text editor this affordance acts on. */
  editor: Editor;
  className?: string;
};

// Below this (plain-text length), there isn't enough to rewrite meaningfully.
const MIN_REWRITE_LENGTH = 12;

/**
 * Unified inline AI affordance for rich-text fields.
 *
 * Server-side and always available (no personal API key required), aware of the
 * resume's target job via {@link useTargetJob} so rewrites are tailored. Works
 * on the editor's HTML so formatting (bullet lists, emphasis) is preserved
 * through Improve / Fix grammar / Tone — and restored exactly on Undo.
 */
export const AiAssist = ({ editor, className }: Props) => {
  const [loading, setLoading] = useState<Action | false>(false);
  // The field HTML as it was just before the most recent AI rewrite, so the
  // user can revert a change they don't like in a single click.
  const [undoValue, setUndoValue] = useState<string | null>(null);
  const { jobDescription } = useTargetJob();

  const html = editor.getHTML();
  const tooShort = editor.getText().trim().length < MIN_REWRITE_LENGTH;
  const jd = jobDescription.trim() || undefined;

  const apply = (next: string) => {
    // Emit the update so RichInput's onUpdate fires and the resume store (and
    // therefore the live preview) stays in sync.
    editor.commands.setContent(next, true);
  };

  const moods: { key: Mood; label: string }[] = [
    { key: "professional", label: t`Professional` },
    { key: "confident", label: t`Confident` },
    { key: "friendly", label: t`Friendly` },
    { key: "casual", label: t`Casual` },
  ];

  const run = async (action: Action, mood?: Mood) => {
    if (tooShort || loading) return;
    const before = html;
    try {
      setLoading(action);

      let result = html;
      if (action === "improve") result = await aiService.improveWriting(html, jd);
      if (action === "fix") result = await aiService.fixGrammar(html);
      if (action === "tone" && mood) result = await aiService.changeTone(html, mood, jd);

      if (result && result !== before) {
        apply(result);
        setUndoValue(before);
      }
    } catch (error) {
      toast({
        variant: "error",
        title: t`Couldn't reach the AI assistant.`,
        description: t`Please try again in a moment.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const busy = !!loading;

  const handleUndo = () => {
    if (undoValue === null || busy) return;
    apply(undoValue);
    setUndoValue(null);
  };

  return (
    <div
      className={cn(
        "mt-3 flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-secondary/30 p-1.5",
        className,
      )}
    >
      <span className="ml-1 mr-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
        <Sparkles size={12} />
        {t`AI`}
      </span>

      <Button
        size="sm"
        variant="ghost"
        disabled={busy || tooShort}
        onClick={() => run("improve")}
        className="h-7 gap-1.5 px-2 text-xs"
      >
        {loading === "improve" ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <WandSparkles size={14} />
        )}
        {t`Improve`}
      </Button>

      <Button
        size="sm"
        variant="ghost"
        disabled={busy || tooShort}
        onClick={() => run("fix")}
        className="h-7 gap-1.5 px-2 text-xs"
      >
        {loading === "fix" ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <SpellCheck size={14} />
        )}
        {t`Fix grammar`}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || tooShort}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            {loading === "tone" ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Drama size={14} />
            )}
            {t`Tone`}
            <ChevronDown size={12} className="opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="z-[60]">
          {moods.map((mood) => (
            <DropdownMenuItem key={mood.key} onClick={() => run("tone", mood.key)}>
              {mood.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {undoValue !== null && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={handleUndo}
          className="ml-auto h-7 gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <Undo2 size={14} />
          {t`Undo AI change`}
        </Button>
      )}

      {tooShort && (
        <span className="ml-auto mr-1 text-[11px] text-muted-foreground">
          {t`Write a little first`}
        </span>
      )}
    </div>
  );
};
